<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewDeductions' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Deductions">
        Deductions
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Deduction</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <div class="crud-form position-relative">

          <div class="form-inner mb-2">
            <div class="form-header d-flex justify-content-between align-items-center">
              <h1 class="mb-0">Deduction</h1>
            </div>

            <div class="deduction-content-area position-relative">
              <div v-if="loading" class="loading-overlay text-center">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading deduction details...</p>
              </div>

              <div v-if="!loading" class="form-body mt-3 mb-3">

                <div class="form-row">
                  <div class="form-row-label">
                    <label for="dedName" class="form-label">Name</label>
                  </div>
                  <div class="form-row-input">
                    <input id="dedName" type="text" class="form-control deduction-input"
                      :value="deduction.name" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label for="dedDescription" class="form-label">Description</label>
                  </div>
                  <div class="form-row-input">
                    <textarea id="dedDescription" class="form-control deduction-input"
                      :value="deduction.description || 'N/A'" readonly></textarea>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Calculation Mode</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control deduction-input"
                      :value="deduction.mode === 1 ? 'Percentage' : 'Fixed Amount'" readonly>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">
                      {{ deduction.mode === 1 ? 'Default Percentage' : 'Default Amount' }}
                    </label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control deduction-input" :value="formattedDefaultValue"
                      readonly>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Federal Deduction</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control deduction-input"
                      :value="deduction.fedInd ? 'Yes' : 'No'" readonly>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Status</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control deduction-input"
                      :value="deduction.activeInd ? 'Active' : 'Inactive'" readonly>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div v-if="!loading" class="action-buttons-container">
            <div class="action-buttons-wrapper">
              <div class="action-button">
                <button type="button" class="btn btn-primary edit-btn">
                  <router-link :to="{ name: 'EditDeduction', params: { id: id } }" class="text-decoration-none"
                    style="color: #fff">
                    Edit Deduction
                  </router-link>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Swal from 'sweetalert2'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const id = route.params.id

const loading = ref(true)
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
const deduction = ref({
  name: '',
  description: '',
  mode: 0,
  fedInd: 0,
  defaultValue: 0,
  activeInd: 1,
})

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return ''
  const num = Number(val)
  if (isNaN(num)) return ''
  return '₦' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formattedDefaultValue = computed(() =>
  deduction.value.mode === 1
    ? `${Number(deduction.value.defaultValue || 0).toFixed(2)}%`
    : formatCurrency(deduction.value.defaultValue)
)

const fetchDeduction = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/payment/deduction-type/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (data?.errorMessage) {
      Swal.fire({
        html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>${data.errorMessage}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      return
    }

    deduction.value.name = data?.name || ''
    deduction.value.description = data?.description || ''
    deduction.value.mode = Number(data?.mode ?? 0)
    deduction.value.fedInd = Number(data?.fedInd ?? 0)
    deduction.value.defaultValue = Number(data?.defaultValue ?? 0)
    deduction.value.activeInd = Number(data?.activeInd ?? 1)
  } catch (error) {
    console.error('Failed to fetch deduction type:', error)
    Swal.fire({
      html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to load deduction details.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchDeduction()
  document.title = 'View Deduction'
})
</script>

<style scoped>
.deduction-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Form Container */
.form-outer {
  width: 100%;
  max-width: 800px;
}

.crud-form {
  background: white;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

/* Form Row Layout */
.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 180px;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
  white-space: nowrap;
}

.form-row-input {
  flex: 1;
}

.deduction-input {
  background: white;
  border: 1px solid #d8d8d8 !important;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  outline: none;
  box-shadow: none !important;
  cursor: default;
}

.deduction-input:focus {
  outline: none;
  box-shadow: none !important;
}

.form-view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  box-shadow: none !important;
}

/* Action Buttons Container */
.action-buttons-container {
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--bg);
}

.action-buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-button {
  flex: 0 0 auto;
}

.action-button button {
  min-width: 140px;
  white-space: nowrap;
}

.btn {
  padding: 0.5rem 1rem !important;
  border-radius: 4px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
}

.edit-btn {
  background-color: #007bff !important;
  border-color: #007bff !important;
}

.edit-btn:hover {
  background-color: #0056b3 !important;
}

/* Tablet Responsiveness (768px - 1024px) */
@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
  }

  .form-row-label {
    flex: 0 0 140px;
  }
}

/* Mobile Responsiveness (below 768px) */
@media screen and (max-width: 768px) {
  .form-header h1 {
    font-size: 1.25rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }

  .form-view-only-input {
    font-size: 0.95rem;
    padding: 0.25rem 0;
    font-weight: 500;
  }

  .action-buttons-container {
    padding: 1rem;
  }

  .action-buttons-wrapper {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-button {
    width: 100%;
  }

  .action-button button {
    width: 100%;
    min-width: 100%;
  }

  .page-identifier {
    font-size: 0.875rem;
  }

  .page-identifier .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

/* Small Mobile (below 480px) */
@media screen and (max-width: 480px) {
  .form-header h1 {
    font-size: 1.125rem;
  }

  .form-row {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .form-row-label label {
    font-size: 0.8125rem;
  }

  .form-view-only-input {
    font-size: 0.875rem;
  }

  .action-buttons-container {
    padding: 0.75rem;
  }

  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}

/* Print Styles */
@media print {

  .action-buttons-container,
  .page-identifier {
    display: none !important;
  }

  .form-outer {
    max-width: 100%;
    box-shadow: none;
  }

  .form-row {
    page-break-inside: avoid;
  }
}
</style>
